import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Icons } from "../../../../../../../shared/icons/icons";

import ClinicImage from "../ClinicImage/ClinicImage";

const STORAGE_KEY = "dionara_clinic_gallery_positions_v3";

export default function ClinicGallery({ gallery }) {
  const allImages = useMemo(() => {
    return [gallery.main, ...gallery.items];
  }, [gallery]);

  const defaultPositions = useMemo(() => {
    return {
      main: gallery.main.id,
      thumbs: gallery.items.map((item) => item.id),
    };
  }, [gallery]);

  const [positions, setPositions] = useState(defaultPositions);
  const [animationKey, setAnimationKey] = useState(0);

  const getImageById = (id) => {
    return allImages.find((image) => image.id === id) || null;
  };

  const validatePositions = (value) => {
    if (!value || typeof value !== "object") return false;
    if (!value.main || !Array.isArray(value.thumbs)) return false;

    const validIds = allImages.map((image) => image.id);
    const positionIds = [value.main, ...value.thumbs];

    const sameLength = positionIds.length === validIds.length;
    const onlyValidIds = positionIds.every((id) => validIds.includes(id));
    const hasAllIds = validIds.every((id) => positionIds.includes(id));
    const noDuplicates = new Set(positionIds).size === positionIds.length;

    return sameLength && onlyValidIds && hasAllIds && noDuplicates;
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);

      if (!saved) {
        setPositions(defaultPositions);
        return;
      }

      const parsed = JSON.parse(saved);

      if (validatePositions(parsed)) {
        setPositions(parsed);
      } else {
        localStorage.removeItem(STORAGE_KEY);
        setPositions(defaultPositions);
      }
    } catch (error) {
      console.error("Erro ao carregar posições da galeria:", error);
      localStorage.removeItem(STORAGE_KEY);
      setPositions(defaultPositions);
    }
  }, [defaultPositions, allImages]);

  const mainImage = getImageById(positions.main) || gallery.main;

  const thumbImages = positions.thumbs
    .map((id) => getImageById(id))
    .filter(Boolean);

  const handleThumbClick = (thumbId) => {
    setPositions((currentPositions) => {
      const updatedPositions = {
        main: thumbId,
        thumbs: currentPositions.thumbs.map((currentThumbId) =>
          currentThumbId === thumbId ? currentPositions.main : currentThumbId
        ),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedPositions));

      return updatedPositions;
    });

    setAnimationKey((currentKey) => currentKey + 1);
  };

  return (
    <div
      className="clinic-section__gallery reveal-on-scroll reveal-on-scroll--right reveal-delay-1"
      aria-label="Fotos do espaço"
    >
      <Link
        to="/space"
        className="clinic-section__gallery-main"
        aria-label="Conhecer o espaço da clínica"
      >
        <div
          key={animationKey}
          className="clinic-section__gallery-main-animation"
        >
          <ClinicImage
            src={mainImage.src}
            alt={mainImage.alt}
            fallback={mainImage.fallback}
          />
        </div>

        <div className="clinic-section__tour-badge reveal-on-scroll reveal-on-scroll--scale reveal-delay-3">
          <strong>
            <Icons.Tour360 />
          </strong>

          <div>
            <b>Tour virtual</b>
            <small>Conheça cada detalhe</small>
          </div>
        </div>
      </Link>

      <div className="clinic-section__gallery-thumbs">
        {thumbImages.map((item, index) => (
          <button
            type="button"
            className="clinic-section__gallery-item clinic-section__gallery-item--active"
            key={`clinic-thumb-position-${index}`}
            onClick={() => handleThumbClick(item.id)}
            aria-label={`Ver ${item.fallback} como imagem principal`}
          >
            <ClinicImage
              src={item.src}
              alt={item.alt}
              fallback={item.fallback}
            />
          </button>
        ))}
      </div>
    </div>
  );
}